import { useEffect } from 'react';
import {
    ButtonPrimary,
    ErrorFeedbackScreen,
    SuccessFeedbackScreen,
    InfoFeedbackScreen,
    skinVars,
    useWindowSize,
    useDialog,
    Stack,
    Text3,
} from '@telefonica/mistica';
import { useTranslation } from 'react-i18next';
import './index.scss';
import { UnifiedStatus, ContentDef } from '../../../../models/OwnershipChange';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useChangeOwnershipContext } from '../../../../context/change-ownership-context/ChangeOwnershipContext';
import { useQueryClient } from '@tanstack/react-query';
import { CHANGE_OWNERSHIP_QUERY_KEY } from '../../../../hooks/useChangeOwnership';

export const FeedbackChangeOwnership = () => {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const statusParam = params.get('status') as UnifiedStatus | null;
    const { dialog } = useDialog();
    const size = useWindowSize();
    const navigate = useNavigate();
    const { flowStartUrl } = useChangeOwnershipContext();
    const queryClient = useQueryClient();

    const closeWindow = () => {
        window.close();
    };

    const MAP: Record<UnifiedStatus, ContentDef> = {
        START: {
            variant: 'success',
            renderType: 'feedback',
            titleKey: 'changeHolderProcessStartedTitle',
            descriptionKeys: 'changeHolderProcessStartedDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            onPrimary: () => navigate(flowStartUrl),
        },
        NO_VERIFIED_CONTACT: {
            variant: 'error',
            renderType: 'feedback',
            titleKey: 'changeHolderNoVerifiedContactTitle',
            descriptionKeys: 'changeHolderNoVerifiedContactDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            withoutErrorCode: true,
            onPrimary: () => navigate(flowStartUrl),
        },
        CONVERGENT_NO_VALID: {
            variant: 'error',
            renderType: 'feedback',
            titleKey: 'changeHolderFailedInterestedTitle',
            descriptionKeys: 'changeHolderConvergentNoValidDescription',
            errorReferencePrefixKey: 'commonErrorCodePrefix',
            primaryButtonTextKey: 'back',
            withoutErrorCode: true,
            onPrimary: () => navigate(flowStartUrl),
        },
        FAILED_INTERESTED: {
            variant: 'error',
            renderType: 'feedback',
            titleKey: 'changeHolderFailedInterestedTitle',
            descriptionKeys: ['changeHolderFailedInterestedDescription'],
            errorReferencePrefixKey: 'commonErrorCodePrefix',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            withoutErrorCode: true,
            onPrimary: () => navigate(flowStartUrl),
        },
        FAILED_PETITIONER: {
            variant: 'error',
            renderType: 'feedback',
            titleKey: 'changeHolderFailedPetitionerTitle',
            descriptionKeys: ['changeHolderFailedPetitionerDescription'],
            errorReferencePrefixKey: 'commonErrorCodePrefix',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            withoutErrorCode: true,
            onPrimary: () => navigate(flowStartUrl),
        },
        SIGNED: {
            variant: 'info',
            renderType: 'feedback',
            titleKey: 'changeHolderCancelConfirmTitle',
            descriptionKeys: 'changeHolderSignedDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            onPrimary: () => navigate(flowStartUrl),
        },
        CANCEL: {
            variant: 'success',
            renderType: 'feedback',
            titleKey: 'changeHolderProcessCanceledTitle',
            descriptionKeys: 'changeHolderProcessCanceledDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            onPrimary: () => {
                navigate(flowStartUrl);
            },
        },
        SING_CONTRACT: {
            variant: 'success',
            renderType: 'feedback',
            titleKey: 'changeHolderSingContractTitle',
            descriptionKeys: 'changeHolderSingContractDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            onPrimary: () => {
                flowStartUrl === '' ? closeWindow() : navigate(flowStartUrl);
            },
        },
        INTERRUPTED_PROCESS: {
            variant: 'error',
            renderType: 'feedback',
            titleKey: 'changeHolderProcessInterruptedTitle',
            descriptionKeys: 'changeHolderProcessInterruptedDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            withoutErrorCode: true,
            onPrimary: () => window.close(),
        },
        DEADLINE_EXPIRED: {
            variant: 'error',
            renderType: 'feedback',
            titleKey: 'changeHolderLinkExpiredTitle',
            descriptionKeys: 'changeHolderLinkExpiredDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            withoutErrorCode: true,
            onPrimary: () => window.close(),
        },
        INVALID_DOCUMENT: {
            variant: 'error',
            renderType: 'feedback',
            titleKey: 'changeHolderGenericErrorTitle',
            descriptionKeys: 'changeHolderIdMismatchDescription',
            primaryButtonTextKey: 'calldivertalerttextbtn',
            withoutErrorCode: true,
            onPrimary: () => window.close(),
        },
    };

    const def = statusParam ? MAP[statusParam] : undefined;

    if (!def) {
        return null;
    }

    const description = Array.isArray(def.descriptionKeys)
        ? def.descriptionKeys.map((k) => t(k))
        : t(def.descriptionKeys);

    const extraNode = def.makeExtra ? def.makeExtra() : undefined;

    const handlePrimaryAction = async () => {
        await queryClient.invalidateQueries({ queryKey: CHANGE_OWNERSHIP_QUERY_KEY });
        def.onPrimary();
    };

    const primaryButton = <ButtonPrimary onPress={handlePrimaryAction}>{t(def.primaryButtonTextKey)}</ButtonPrimary>;

    const isSuccess = def.variant === 'success';

    useEffect(() => {
        if (def.renderType === 'dialog') {
            handleInfoDialog();
        }
    }, []);
    const screen =
        def.variant === 'success' ? (
            <SuccessFeedbackScreen
                title={t(def.titleKey)}
                description={description}
                primaryButton={primaryButton}
                extra={extraNode}
            />
        ) : def.variant === 'error' ? (
            <ErrorFeedbackScreen
                title={t(def.titleKey)}
                description={description}
                primaryButton={primaryButton}
                errorReference={
                    def.withoutErrorCode
                        ? ''
                        : def.errorReferencePrefixKey
                          ? `${t(def.errorReferencePrefixKey)} ${def.errorCode}`
                          : ''
                }
            />
        ) : (
            <InfoFeedbackScreen
                title={t(def.titleKey)}
                description={description}
                primaryButton={primaryButton}
                extra={extraNode}
            />
        );
    const toArray = (value: string | string[]) => (Array.isArray(value) ? value : [value]);
    const handleInfoDialog: any = () => {
        dialog({
            asset: def.icon,
            title: t(def.titleKey),
            message: (() => {
                const lines = toArray(def.descriptionKeys);
                return (
                    <Stack space={0}>
                        {lines.map((key, idx) => (
                            <Text3 key={key ?? idx} light color={skinVars.colors.textSecondary}>
                                {t(key)}
                            </Text3>
                        ))}
                    </Stack>
                );
            })() as any,
            acceptText: t('buttonUnderstoodFeedback'),
            onAccept: () => {},
            ...(def.onCancel ? { onCancel: () => {} } : {}),
        });
    };

    return (
        <>
            {def.renderType === 'feedback' && (
                <div
                    className='changeHolder-feedback'
                    style={{
                        background: isSuccess
                            ? size.width < 1024 || size.height < 550
                                ? skinVars.colors.backgroundBrand
                                : ''
                            : skinVars.colors.background,
                    }}
                >
                    {screen}
                </div>
            )}

            {def.renderType === 'dialog' && <div className='changeHolder-dialog'></div>}
        </>
    );
};
export default FeedbackChangeOwnership;
